import type { Localization } from '@mui/material/locale';
import * as coreLocales from '@mui/material/locale';
import * as dataGridLocales from '@mui/x-data-grid-pro/locales';
import * as datePickerLocales from '@mui/x-date-pickers-pro/locales';

import 'dayjs/locale/es';
import 'dayjs/locale/es-mx';
import 'dayjs/locale/es-us';
import 'dayjs/locale/en';
import 'dayjs/locale/en-gb';
import 'dayjs/locale/en-ca';
import 'dayjs/locale/de';
import 'dayjs/locale/de-ch';
import 'dayjs/locale/de-at';
import 'dayjs/locale/fr';
import 'dayjs/locale/fr-ca';

const supportedLocales = [
  'en',
  'en-gb',
  'en-ca',
  'en-us',
  'es',
  'es-es',
  'es-mx',
  'es-us',
  'de',
  'de-de',
  'de-at',
  'de-ch',
  'fr',
  'fr-fr',
  'fr-ca',
] as const;

export type SupportedLocale = (typeof supportedLocales)[number];

export type LocaleMapper = {
  [key: string]: {
    core: Localization;
    dataGrid: typeof dataGridLocales.enUS;
    datePicker:
      | typeof datePickerLocales.DEFAULT_LOCALE
      | typeof datePickerLocales.enUS;
    dayJsAdapter: SupportedLocale;
  };
};

export const isSupportedLocale = (value: string): value is SupportedLocale => {
  return supportedLocales.includes(value as SupportedLocale);
};

export const localeMapper: LocaleMapper = {
  ...['en', 'en-gb', 'en-ca', 'en-us'].reduce((acc, locale) => {
    const dayJsAdapter = locale === 'en-us' ? 'en' : locale;
    return {
      ...acc,
      [locale]: {
        core: coreLocales.enUS,
        dataGrid: dataGridLocales.enUS,
        datePicker: datePickerLocales.enUS,
        dayJsAdapter,
      },
    };
  }, {}),
  ...['fr', 'fr-ca', 'fr-fr'].reduce((acc, locale) => {
    const dayJsAdapter = locale === 'fr-fr' ? 'fr' : locale;
    return {
      ...acc,
      [locale]: {
        core: coreLocales.frFR,
        dataGrid: dataGridLocales.frFR,
        datePicker: datePickerLocales.frFR,
        dayJsAdapter,
      },
    };
  }, {}),
  ...['de', 'de-at', 'de-ch'].reduce((acc, locale) => {
    const dayJsAdapter = locale === 'de-de' ? 'de' : locale;
    return {
      ...acc,
      [locale]: {
        core: coreLocales.deDE,
        dataGrid: dataGridLocales.deDE,
        datePicker: datePickerLocales.deDE,
        dayJsAdapter,
      },
    };
  }, {}),
  ...['es', 'es-es', 'es-mx', 'es-us'].reduce((acc, locale) => {
    const dayJsAdapter = locale === 'es-es' ? 'es' : locale;
    return {
      ...acc,
      [locale]: {
        core: coreLocales.esES,
        dataGrid: dataGridLocales.esES,
        datePicker: datePickerLocales.esES,
        dayJsAdapter,
      },
    };
  }, {}),
};
